// src/features/stock-info/StockInfoPage.tsx
//
// Stock Info Deep-Dive Page — Component Islands architecture for
// progressive loading and failure isolation. Extracts the symbol from
// URL query param (?symbol=AAPL), concurrently fetches Overview data
// (quote, profile, targets, grades) from GET /api/v1/stock-info/{symbol}/overview
// and renders a beautiful institutional-grade header with KPI widgets.
//
// Future phases will add:
// - Interactive intraday charts (Recharts)
// - Financial tabs (Key Metrics, Ratios, Growth, Estimates)
// - Polars-ranked peers table
//
// Each component island fetches its own data independently so one slow/failing
// endpoint doesn't block the whole page.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout, Sidebar } from '../../shared/ui';
import {
    resolveCurrency,
    formatMoney,
    formatMarketCap,
} from '../../shared/utils/currency';
import { fetchStockOverview, type StockOverview } from './api';
import { IntradayChart } from './components/IntradayChart';
import { PeersTable } from './components/PeersTable';
import { FinancialTabs } from './components/FinancialTabs';
import { GradesConsensusChart } from './components/GradesConsensusChart';
import { EarningsChart } from './components/EarningsChart';
import { ShareFloatChart } from './components/ShareFloatChart';
import { ValuationRatiosTable } from './components/ValuationRatiosTable';
import { BreakoutAlertWidget } from '../breakout-alerts/components/BreakoutAlertWidget';
import { usePortfolioStore } from '../user-portfolios/store/usePortfolioStore';

export const StockInfoPage = () => {
    const navigate = useNavigate();
    // Zustand: opens the global "Add to Portfolio" modal. Selecting only this
    // slice means the page does not re-render when the modal opens/closes.
    const openModal = usePortfolioStore((s) => s.openModal);
    
    // Extract the symbol from the URL (?symbol=AAPL)
    const [searchParams] = useSearchParams();
    const symbol = searchParams.get('symbol')?.toUpperCase();

    // "Back to Rankings": prefer real history so in-app navigations restore the
    // caller's scroll/filter state. When the page is the FIRST history entry
    // (React Router sets location.key === 'default' on a fresh tab / direct
    // load -- e.g. the Top Performers ticker link opens in a new tab), there is
    // nothing to go back to, so fall back to the Stock Ranking page.
    const location = useLocation();
    const handleBackToRankings = () => {
        if (location.key && location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/dashboard/stock-ranking');
        }
    };

    // Component State
    const [overview, setOverview] = useState<StockOverview | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch Overview Data on Mount
    useEffect(() => {
        if (!symbol) return;
        
        let cancelled = false;
        setIsLoading(true);
        setError(null);

        fetchStockOverview(symbol)
            .then(data => {
                if (!cancelled) setOverview(data);
            })
            .catch((err) => {
                if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    setError(`Unable to load data for ${symbol}. Please try again.`);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, [symbol]);

    // Resolve the listing currency ONCE (authoritative backend value first,
    // then symbol/exchange fallback) and thread it through every money format
    // so EU (.DE/.VI) names render in EUR instead of a hardcoded USD.
    const currency = resolveCurrency({
        explicitCurrency: overview?.quote?.currency,
        symbol,
        exchange: overview?.quote?.exchange ?? overview?.profile?.exchange,
    });

    // Formatters for institutional look
    const formatCurrency = (val?: number) =>
        formatMoney(val, { explicitCurrency: currency }, { fallback: 'N/A' });
    const formatPercent = (val?: number) => val != null ? `${val > 0 ? '+' : ''}${val.toFixed(2)}%` : 'N/A';

    if (!symbol) {
        return (
            <Layout sidebar={<Sidebar />} title="Stock Intelligence">
                <div className="p-6 text-slate-500 dark:text-slate-400">
                    No symbol provided. Please select a stock from the Screener.
                </div>
            </Layout>
        );
    }

    return (
        <Layout
            sidebar={<Sidebar />}
            title={`Deep Dive: ${symbol}`}
            subtitle={overview?.profile?.company_name || "Advanced Institutional Analysis"}
        >
            <div className="mb-6">
                <button 
                    onClick={handleBackToRankings} 
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                    &larr; Back to Rankings
                </button>
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse">
                    Loading quantitative data for {symbol}…
                </div>
            ) : overview && (
                <div className="space-y-6">
                    {/* --- HEADER ISLAND: Identity & Live Quote --- */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 border-l-4 border-l-blue-500">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                                        {overview.profile.company_name || symbol}
                                    </h1>
                                    <span className="px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-mono font-bold rounded border border-slate-200 dark:border-slate-700">
                                        {overview.quote.exchange || 'N/A'} : {symbol}
                                    </span>
                                </div>
                                <p className="text-slate-500 dark:text-slate-400 text-sm">
                                    {overview.profile.sector} • {overview.profile.industry}
                                </p>
                            </div>

                            <div className="mt-4 md:mt-0 text-left md:text-right">
                                <div className="text-4xl font-mono font-bold text-slate-900 dark:text-white">
                                    {formatCurrency(overview.quote.price)}
                                </div>
                                <div className={`text-lg font-medium mt-1 flex items-center md:justify-end gap-1 ${
                                    (overview.quote.change ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                                }`}>
                                    {(overview.quote.change ?? 0) >= 0 ? '▲' : '▼'}
                                    {Math.abs(overview.quote.change ?? 0).toFixed(2)} ({formatPercent(overview.quote.changes_percentage)})
                                </div>
                                <button
                                    onClick={() =>
                                        openModal({
                                            symbol,
                                            name: overview.profile.company_name || symbol,
                                            type: 'STOCK',
                                            sector: overview.profile.sector ?? null,
                                            currentPrice: overview.quote.price ?? 0,
                                            exchange:
                                                overview.quote.exchange ??
                                                overview.profile.exchange,
                                            currency,
                                        })
                                    }
                                    className="mt-3 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 transition"
                                >
                                    + Add to Portfolio
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* --- ACTIONABLE ALGORITHMIC BREAKOUT ALERT ISLAND --- */}
                    <BreakoutAlertWidget symbol={symbol} />

                    {/* --- KPI WIDGETS --- */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Market Cap</p>
                            <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                {formatMarketCap(overview.quote.market_cap, currency)}
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Target Price per Share</p>
                            <div className="flex items-end gap-2 mt-1">
                                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                    {formatCurrency(overview.target.target_consensus)}
                                </p>
                                <span className="text-sm text-slate-500 dark:text-slate-400 mb-1 font-medium">
                                    ({overview.target.consensus || 'N/A'})
                                </span>
                            </div>
                        </div>

                        {/* Calculated Discount/Premium from Backend Pydantic Model */}
                        <div className={`rounded-xl border bg-white dark:bg-slate-800 p-5 border-l-4 ${
                            (overview.discount_premium_pct ?? 0) > 0 
                                ? 'border-l-emerald-500 border-t-slate-200 border-r-slate-200 border-b-slate-200 dark:border-t-slate-700 dark:border-r-slate-700 dark:border-b-slate-700' 
                                : 'border-l-red-500 border-t-slate-200 border-r-slate-200 border-b-slate-200 dark:border-t-slate-700 dark:border-r-slate-700 dark:border-b-slate-700'
                        }`}>
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Current Pricing</p>
                            <p className={`text-xl font-bold mt-1 ${
                                (overview.discount_premium_pct ?? 0) > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
                            }`}>
                                {overview.discount_premium_pct != null 
                                    ? `${Math.abs(overview.discount_premium_pct).toFixed(2)}% ${overview.discount_premium_pct > 0 ? 'Discount (Upside)' : 'Premium (Downside)'}` 
                                    : 'N/A'
                                }
                            </p>
                        </div>

                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Quant Grade</p>
                            <div className="text-2xl font-bold mt-1 text-amber-600 dark:text-amber-500 uppercase">
                                {overview.grades.consensus || 'N/A'}
                            </div>
                        </div>
                    </div>

                    {/* --- ADDITIONAL STATS (Beta, Dividend, Volume, ISIN) --- */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Beta</p>
                            <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                {overview.profile.beta != null ? overview.profile.beta.toFixed(2) : 'N/A'}
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Last Dividend</p>
                            <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                {formatCurrency(overview.profile.last_dividend)}
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">Average Volume</p>
                            <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">
                                {overview.quote.avg_volume != null ? overview.quote.avg_volume.toLocaleString() : 'N/A'}
                            </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
                            <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">ISIN</p>
                            <div className="text-lg font-bold mt-1 text-slate-900 dark:text-white font-mono break-all">
                                {overview.profile.isin || 'N/A'}
                            </div>
                        </div>
                    </div>

                    {/* --- ANALYST GRADES & EARNINGS GRID --- */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                        {/* Left: analyst rating distribution (reuses overview.grades) */}
                        <GradesConsensusChart grades={overview.grades} />

                        {/* Right: earnings actual vs estimated with EPS/Revenue toggle */}
                        <EarningsChart symbol={symbol} currency={currency} />
                    </div>

                    {/* --- SHARE FLOAT & VALUATION RATIOS GRID --- */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
                        {/* Left: free-float vs institutional ownership pie */}
                        <ShareFloatChart symbol={symbol} />

                        {/* Right: TTM valuation ratios table */}
                        <ValuationRatiosTable symbol={symbol} />
                    </div>

                    {/* --- BUSINESS DESCRIPTION --- */}
                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Business Profile</h2>
                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                            {overview.profile.description || 'No business description available.'}
                        </p>
                    </div>

                    {/* --- INTERACTIVE CHARTS & PEERS GRID --- */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-stretch">
                        {/* Chart gets 2/3 width on large screens */}
                        <div className="xl:col-span-2">
                            <IntradayChart symbol={symbol} currency={currency} />
                        </div>

                        {/* Peers gets 1/3 width; matches the chart height on xl */}
                        <div className="xl:col-span-1 h-[440px] xl:h-auto">
                            <PeersTable symbol={symbol} currency={currency} />
                        </div>
                    </div>

                    {/* --- FINANCIAL TABS, TABLES, & TREND CHARTS --- */}
                    <FinancialTabs symbol={symbol} />
                </div>
            )}
        </Layout>
    );
};

export default StockInfoPage;
